<template>
  <section class="customer-detail-summary">
    <div class="customer-detail-summary-main">
      <article class="customer-detail-card customer-detail-card--accent-primary">
        <header class="customer-detail-card-header customer-detail-card-header--inline">
          <h4>
            <i class="fa fa-building customer-section-icon customer-section-icon--info" aria-hidden="true"></i>
            Información general
          </h4>

          <button
            type="button"
            class="app-table-icon-btn app-table-icon-btn--edit customer-section-edit-btn customer-section-edit-btn--info"
            title="Editar información general"
            @click="$emit('edit-section', 'general')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="customer-detail-info-grid">
          <div
            v-for="item in summaryGeneral"
            :key="item.label"
            class="customer-detail-info-item"
          >
            <span>{{ item.label }}</span>
            <button
              v-if="item.isAction"
              type="button"
              class="customer-detail-info-action"
              @click="handleSummaryItemAction(item)"
            >
              {{ item.value }}
            </button>
            <span v-else class="customer-detail-info-value">{{ item.value }}</span>
          </div>
        </div>
      </article>

      <PeopleContactsCard
        :contacts="summaryContacts"
        @add-contact="$emit('add-contact')"
        @edit-contact="$emit('edit-contact', $event)"
        @delete-contact="$emit('delete-contact', $event)"
      />
    </div>

    <aside class="customer-detail-summary-side">
      <ContributorBillingDataCard
        :items="summaryBillingGrid"
        :residence="summaryBillingResidence"
        @open-billing-data-list="$emit('open-billing-data-list')"
        @create-billing-data="$emit('create-billing-data')"
        @edit-billing-data="$emit('edit-billing-data')"
      />

      <article class="customer-detail-card customer-detail-card--accent-cyan">
        <header class="customer-detail-card-header customer-detail-card-header--inline">
          <h4>
            <i class="fa fa-map-marker customer-section-icon customer-section-icon--address" aria-hidden="true"></i>
            Dirección
          </h4>

          <button
            type="button"
            class="app-table-icon-btn app-table-icon-btn--edit customer-section-edit-btn customer-section-edit-btn--address"
            title="Editar dirección"
            @click="$emit('edit-section', 'address')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="customer-detail-info-stack">
          <div
            v-for="item in summaryAddress"
            :key="item.label"
            class="customer-detail-info-item"
          >
            <span>{{ item.label }}</span>
            <button
              v-if="item.isAction"
              type="button"
              class="customer-detail-info-action"
              @click="handleSummaryItemAction(item)"
            >
              {{ item.value }}
            </button>
            <span v-else class="customer-detail-info-value">{{ item.value }}</span>
          </div>
        </div>
      </article>

      <article class="customer-detail-card customer-detail-card--accent-warning customer-detail-card--comments">
        <header class="customer-detail-card-header customer-detail-card-header--inline">
          <h4>
            <i class="fa fa-commenting-o customer-section-icon customer-section-icon--comments" aria-hidden="true"></i>
            Comentarios
          </h4>

          <button
            type="button"
            class="app-table-icon-btn app-table-icon-btn--edit customer-section-edit-btn customer-section-edit-btn--comments"
            title="Editar comentarios"
            @click="$emit('edit-section', 'general')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="customer-detail-comments">
          {{ summaryComment }}
        </div>
      </article>
    </aside>
  </section>
</template>

<script>
import { buildInitials, formatCodeName, valueOrDash } from '@/utils/textValue';
import { normalizeStatusLabel } from '@/utils/dataNormalization';
import { useDomainText } from '@/composables/text/useDomainText';
import ContributorBillingDataCard from '@/components/contributors/ContributorBillingDataCard.vue';
import PeopleContactsCard from '@/components/people/detail/PeopleContactsCard.vue';

const { catalog: suppliersText } = useDomainText('suppliers');

const joinSpecialties = (supplier) => {
  const list = Array.isArray(supplier?.specialties)
    ? supplier.specialties
    : [];

  const names = list
    .map((item) => String(item?.name || item?.label || item || '').trim())
    .filter(Boolean);

  return names.length > 0 ? names.join(', ') : '';
};

export default {
  name: 'SupplierDetailSummaryTab',
  components: {
    ContributorBillingDataCard,
    PeopleContactsCard,
  },
  props: {
    supplier: {
      type: Object,
      default: null,
    },
    persons: {
      type: Array,
      default: () => [],
    },
    defaultBillingData: {
      type: Object,
      default: null,
    },
  },
  emits: [
    'edit-section',
    'add-contact',
    'edit-contact',
    'delete-contact',
    'edit-billing-data',
    'open-billing-data-list',
    'create-billing-data',
  ],
  data() {
    return {
      text: suppliersText.detail.informationTab,
    };
  },
  computed: {
    supplierName() {
      return String(this.supplier?.name || '').trim();
    },
    supplierStatusLabel() {
      return normalizeStatusLabel(this.supplier?.active);
    },
    summaryGeneral() {
      return [
        this.resolveSummaryItem('Nombre', this.supplierName, 'general'),
        this.resolveSummaryItem('Clase', this.supplier?.class?.name || this.supplier?.class_name, 'general'),
        this.resolveSummaryItem('Categoría', this.supplier?.category?.name || this.supplier?.category_name, 'general'),
        this.resolveSummaryItem('Especialidades', joinSpecialties(this.supplier), 'classification'),
        this.resolveSummaryItem('Área', this.supplier?.area?.name || this.supplier?.area_name, 'classification'),
        this.resolveSummaryItem('Sitio web', this.supplier?.website, 'general'),
        this.resolveSummaryItem('Estado', this.supplierStatusLabel, 'general'),
        this.resolveSummaryItem('Email', this.supplier?.email, 'contact'),
        this.resolveSummaryItem('Teléfono', this.supplier?.phone, 'contact'),
        this.resolveSummaryItem('Portafolio', this.supplier?.portfolio, 'contact'),
      ];
    },
    summaryBillingGrid() {
      const billingData = this.defaultBillingData || this.supplier?.billing_data || {};

      return [
        this.resolveSummaryItem(
          'Razón social',
          billingData?.name || billingData?.business_name || this.supplier?.fiscal_name || this.supplierName,
          'billing'
        ),
        this.resolveSummaryItem('RFC', billingData?.rfc || this.supplier?.rfc, 'billing'),
        this.resolveSummaryItem(
          'Uso CFDI',
          formatCodeName(billingData?.uso_cfdi, billingData?.uso_cfdi_name, billingData?.uso_cfdi_label),
          'billing'
        ),
        this.resolveSummaryItem(
          'Régimen fiscal',
          formatCodeName(billingData?.regimen, billingData?.regimen_name, billingData?.regimen_label),
          'billing'
        ),
        this.resolveSummaryItem(
          'Forma de pago',
          formatCodeName(billingData?.payment_method, billingData?.payment_method_name, billingData?.payment_method_label),
          'billing'
        ),
        this.resolveSummaryItem(
          'Método de pago',
          formatCodeName(
            billingData?.payment_method_type,
            billingData?.payment_method_type_name,
            billingData?.payment_method_type_label
          ),
          'billing'
        ),
      ];
    },
    summaryBillingResidence() {
      const billingData = this.defaultBillingData || this.supplier?.billing_data || {};
      return this.resolveSummaryItem('Residencia fiscal', this.buildFiscalResidence(billingData), 'billing');
    },
    summaryAddress() {
      return [
        this.resolveSummaryItem('Dirección', this.supplier?.address, 'address'),
        this.resolveSummaryItem('País', this.supplier?.country, 'address'),
        this.resolveSummaryItem('Estado', this.supplier?.state, 'address'),
        this.resolveSummaryItem('Ciudad', this.supplier?.city, 'address'),
        this.resolveSummaryItem('Código postal', this.supplier?.zipcode || this.supplier?.zip_code, 'address'),
      ];
    },
    summaryComment() {
      const comment = String(this.supplier?.comments || this.supplier?.comment || '').trim();
      return comment !== '' ? comment : this.text.comments.empty;
    },
    summaryContacts() {
      const list = Array.isArray(this.persons) ? this.persons.slice() : [];

      if (list.length === 0) {
        return [];
      }

      list.sort((left, right) => {
        const leftDefault = Number(left?.default || 0);
        const rightDefault = Number(right?.default || 0);

        if (leftDefault !== rightDefault) {
          return rightDefault - leftDefault;
        }

        return Number(left?.id || 0) - Number(right?.id || 0);
      });

      const hasDefault = list.some((contact) => Number(contact?.default || 0) === 1);

      return list.map((contact, index) => {
        const name = String(contact?.name || '').trim() || '-';
        const role = String(contact?.job_title || contact?.position || contact?.role || '').trim() || 'Sin rol';
        const phone = String(
          contact?.cell_phone
          || contact?.office_phone
          || contact?.home_phone
          || contact?.phone
          || ''
        ).trim() || '-';

        return {
          key: `${contact?.id || index}-${name}`,
          name,
          role,
          email: valueOrDash(contact?.email),
          phone,
          initials: buildInitials(name, { fallback: 'CT' }),
          isDefault: Number(contact?.default || 0) === 1 || (!hasDefault && index === 0),
          raw: contact,
        };
      });
    },
  },
  methods: {
    resolveSummaryItem(label, value, sectionKey = 'general') {
      const normalized = String(value ?? '').trim();
      const isAction = normalized === '' || normalized === '-';

      return {
        label,
        value: isAction ? this.text.common.assign : normalized,
        isAction,
        sectionKey,
      };
    },
    handleSummaryItemAction(item) {
      const sectionKey = String(item?.sectionKey || '').trim().toLowerCase();

      if (sectionKey === 'billing') {
        this.$emit('edit-billing-data');
        return;
      }

      this.$emit('edit-section', sectionKey || 'general');
    },
    buildFiscalResidence(billingData) {
      const zipcode = String(billingData?.zipcode || '').trim();

      const streetSegments = [
        String(billingData?.street || '').trim(),
        String(billingData?.exterior || '').trim(),
        String(billingData?.interior || '').trim(),
      ].filter(Boolean);

      const addressSegments = [
        streetSegments.join(' '),
        String(billingData?.neighborhood || '').trim(),
        String(billingData?.municipality || '').trim(),
        String(billingData?.city || '').trim(),
        String(billingData?.state_name || billingData?.state || '').trim(),
        String(billingData?.country_name || billingData?.country || '').trim(),
      ].filter(Boolean);

      const fullAddress = addressSegments.join(', ');

      if (zipcode !== '' && fullAddress !== '') {
        return `${zipcode} - ${fullAddress}`;
      }

      if (zipcode !== '') {
        return zipcode;
      }

      return fullAddress;
    },
  },
};
</script>
